import {queries} from '@/shared/integrations/zero'
import {Section} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type SectionStartRow = Pick<Section, 'id' | 'createdAt' | 'name'>

export function useGetInfiniteSections({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<Parameters<typeof queries.locations.listSections>[0]>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.locationId, filters?.wardId, filters?.name, limit, search]
  )

  const getPageQuery: GetPageQuery<Section, SectionStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              name: start.name,
            }
          : undefined

      return {
        query: queries.locations.listSections({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, name: search || filters?.name},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<Section> = useCallback(
    ({id}) => ({
      query: queries.locations.getSectionById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: Section): SectionStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: Section) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
